import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@ohif/ui';
import { utils } from '@ohif/core';
import { useTranslation } from 'react-i18next';

const { formatDate, formatPN } = utils;

const formatWithEllipsis = (str, maxLength) => {
  if (str?.length > maxLength) {
    return str.substring(0, maxLength) + '...';
  }
  return str;
};

function usePatientInfo(servicesManager: AppTypes.ServicesManager) {
  const { displaySetService } = servicesManager.services;
  const [patientInfo, setPatientInfo] = useState({
    PatientName: '',
    PatientID: '',
    PatientSex: '',
    PatientDOB: '',
  });
  const [isMixedPatients, setIsMixedPatients] = useState(false);
  const displaySets = displaySetService.getActiveDisplaySets();

  const checkMixedPatients = PatientID => {
    const displaySets = displaySetService.getActiveDisplaySets();
    let isMixedPatients = false;
    displaySets.forEach(displaySet => {
      const instance = displaySet?.instances?.[0] || displaySet?.instance;
      if (!instance) {
        return;
      }
      if (instance.PatientID !== PatientID) {
        isMixedPatients = true;
      }
    });
    setIsMixedPatients(isMixedPatients);
  };

  const updatePatientInfo = () => {
    const displaySet = displaySets[0];
    const instance = displaySet?.instances?.[0] || displaySet?.instance;
    if (!instance) {
      return;
    }
    setPatientInfo({
      PatientID: instance.PatientID || null,
      PatientName: instance.PatientName ? formatPN(instance.PatientName.Alphabetic) : null,
      PatientSex: instance.PatientSex || null,
      PatientDOB: formatDate(instance.PatientBirthDate) || null,
    });
    checkMixedPatients(instance.PatientID || null);
  };

  useEffect(() => {
    const subscription = displaySetService.subscribe(
      displaySetService.EVENTS.DISPLAY_SETS_ADDED,
      () => updatePatientInfo()
    );
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    updatePatientInfo();
  }, [displaySets]);

  return { patientInfo, isMixedPatients };
}

function HeaderPatientInfo({ servicesManager }: withAppTypes) {
  const { t } = useTranslation();
  const { patientInfo, isMixedPatients } = usePatientInfo(servicesManager);

  const formattedPatientName = formatWithEllipsis(patientInfo.PatientName, 27);
  const formattedPatientID = formatWithEllipsis(patientInfo.PatientID, 15);

  return (
    <div className="text-common-light flex min-w-0 items-center justify-center gap-1.5 rounded-lg px-2 py-1">
      <Icon
        name={isMixedPatients ? 'icon-multiple-patients' : 'icon-patient'}
        className="text-common-light"
      />
      <div className="flex min-w-0 flex-col justify-center">
        {!isMixedPatients ? (
          <>
            <div className="max-w-full self-start truncate text-[13px] font-bold text-white">
              {formattedPatientName}
            </div>
            <div className="text-common-light flex max-w-full gap-2 overflow-hidden whitespace-nowrap text-[11px]">
              <div className="truncate">{formattedPatientID}</div>
              <div className="shrink-0">{patientInfo.PatientSex}</div>
              <div className="shrink-0">{patientInfo.PatientDOB}</div>
            </div>
          </>
        ) : (
          <div className="text-common-bright self-center text-[13px] font-medium">
            {isMixedPatients ? t('Header:Multiple Patients') : t('Header:Patient')}
          </div>
        )}
      </div>
    </div>
  );
}

HeaderPatientInfo.propTypes = {
  servicesManager: PropTypes.object.isRequired,
};

export default HeaderPatientInfo;
